import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useGreenhouseForm } from '@Router/EsgGreenhouse/Containers/EsgGreenhouseFormProvider';
import type { IOtherDetailFormValues } from '@Router/EsgGreenhouse/Containers/EsgGreenhouseFormProvider/types';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useGreenhouseGoalTerm } from '../../GreenhouseGoalTermProvider/GreenhouseGoalTermProvider';
import OtherGreenhouseGasBaseYear from './OtherGreenhouseGasBaseYear';
import OtherGreenhouseGasGoalYear from './OtherGreenhouseGasGoalYear';
import OtherGreenhouseGasScope from './OtherGreenhouseGasScope';
import {
  filterScopeOptions,
  isGoalYearComplete,
  mapOtherGreenhouseGasFormValue,
} from './utils';
import { OtherGreenhouseGasModalSchema } from './validators';

type ModalOtherGreenhouseGasProps = {
  open: boolean;
  close: () => void;
  mode: 'create' | 'edit';
  editData?: IOtherDetailFormValues;
  editIndex?: number;
};

const ModalOtherGreenhouseGas = ({
  open,
  close,
  mode,
  editData,
  editIndex,
}: ModalOtherGreenhouseGasProps) => {
  const initialValues =
    mode === 'edit' && editData
      ? mapOtherGreenhouseGasFormValue(editData)
      : undefined;

  const { ghGasScope } = useGreenhouseGoalTerm();
  const { setValue, getValues, trigger } = useGreenhouseForm();

  const modalOtherGreenhouseGasContext = useForm<IOtherDetailFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(OtherGreenhouseGasModalSchema),
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { isValid },
    watch,
    reset,
  } = modalOtherGreenhouseGasContext;

  const completedSection = useWatch({
    name: 'greenhouseGoalTerm.completedSection',
  });

  const { isCompletedTh, isCompletedEn } = completedSection ?? {};

  const nearTermTarget = watch('nearTermTarget');
  const longTermTarget = watch('longTermTarget');

  const isGoalComplete = isGoalYearComplete(nearTermTarget, longTermTarget);

  const currentScope = (getValues('greenhouseGoalTerm.other.details') || [])
    .map((item) => item?.scopeId)
    .filter((id): id is number => id !== undefined);

  const filteredScopes = filterScopeOptions(
    ghGasScope || [],
    currentScope,
    mode === 'edit' ? editData?.scopeId : undefined
  );

  const handleSave = (data: IOtherDetailFormValues) => {
    const selectedScope = ghGasScope?.find(
      (option) => option.id === data.scopeId
    );

    if (selectedScope) {
      data.scopeId = selectedScope.id;
    }

    const prevData = getValues('greenhouseGoalTerm.other.details') || [];
    const updatedData =
      mode === 'edit' && editIndex !== undefined
        ? prevData.map((item, index) => (index === editIndex ? data : item))
        : [...prevData, data];

    setValue('greenhouseGoalTerm.other.details', updatedData);
    trigger('greenhouseGoalTerm.other.details');
    reset();
    close();
  };

  const handleClose = () => {
    reset();
    close();
  };

  return (
    <FormProvider {...modalOtherGreenhouseGasContext}>
      <Dialog open={open} maxWidth="md">
        <DialogTitle fontWeight={800} fontSize={16}>
          {mode === 'edit'
            ? 'แก้ไขรายละเอียดการตั้งเป้าหมายการลดก๊าซเรือนกระจกอื่น ๆ'
            : 'เพิ่มรายละเอียดการตั้งเป้าหมายการลดก๊าซเรือนกระจกอื่น ๆ'}
        </DialogTitle>
        <DialogContent>
          <OtherGreenhouseGasScope
            options={filteredScopes}
            isCompletedTh={isCompletedTh}
            isCompletedEn={isCompletedEn}
          />
          <OtherGreenhouseGasBaseYear
            isCompletedTh={isCompletedTh}
            isCompletedEn={isCompletedEn}
          />
          <OtherGreenhouseGasGoalYear
            isCompletedTh={isCompletedTh}
            isCompletedEn={isCompletedEn}
          />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={handleClose}>
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            disabled={!isValid || !isGoalComplete}
            onClick={handleSubmit(handleSave)}
          >
            ยืนยัน
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export default ModalOtherGreenhouseGas;
